import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Quote, Star, ChevronLeft, ChevronRight, Facebook } from 'lucide-react';
import { REVIEWS, REVIEW_STATS } from '../data/reviews';

// Simple star row — filled/half/empty
const StarRow = ({ value = 5 }) => (
  <div className="flex items-center gap-0.5" aria-label={`${value} out of 5 stars`}>
    {[0, 1, 2, 3, 4].map((i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < Math.round(value) ? 'fill-yellow-400 stroke-yellow-400' : 'fill-gray-200 stroke-gray-300'}`}
      />
    ))}
  </div>
);

// Small source badge — Facebook icon or the RMA wordmark
const SourceBadge = ({ source }) => {
  if (source === 'Facebook') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-500">
        <Facebook className="w-3.5 h-3.5" /> Facebook
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-gray-500">
      <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-sm bg-red-600 text-white text-[9px] font-bold leading-none">R</span>
      Rate My Agent
    </span>
  );
};

// Custom lightweight carousel. Uses window width to pick 1/2/3 slides per view.
const ClientReviewsCarousel = () => {
  const [index, setIndex] = useState(0); // current "page" (0 .. pages-1)
  const [perView, setPerView] = useState(3);
  const [paused, setPaused] = useState(false);

  // Responsive slides-per-view — recompute on resize
  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      setPerView(w < 768 ? 1 : w < 1024 ? 2 : 3);
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);

  const pages = Math.max(1, REVIEWS.length - perView + 1);

  // Clamp current index whenever perView changes
  useEffect(() => {
    if (index > pages - 1) setIndex(0);
  }, [index, pages]);

  const goPrev = useCallback(() => {
    setIndex((i) => (i - 1 + pages) % pages);
  }, [pages]);
  const goNext = useCallback(() => {
    setIndex((i) => (i + 1) % pages);
  }, [pages]);

  // Auto-advance every 7 seconds — paused when hovered or off-tab
  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => {
      if (document.hidden) return;
      setIndex((i) => (i + 1) % pages);
    }, 7000);
    return () => clearInterval(t);
  }, [pages, paused]);

  // Width of each slide as a percentage of the track width
  const slideBasis = useMemo(() => 100 / perView, [perView]);
  // Amount to translate the track: each page moves by one slide's basis
  const translate = index * slideBasis;

  return (
    <section className="bg-gray-50 border-y border-gray-200" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 py-16">
        {/* Header */}
        <div className="text-center mb-10">
          <p className="text-red-500 uppercase tracking-widest text-xs font-semibold mb-3">Client Reviews</p>
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3">
            What clients say about working with <span className="text-red-600">Alexander</span>
          </h2>
          <div className="inline-flex items-center gap-2 text-sm text-gray-600">
            <StarRow value={REVIEW_STATS.averageRating} />
            <span className="font-semibold text-gray-900">{REVIEW_STATS.averageRating.toFixed(1)}</span>
            <span>·</span>
            <span>{REVIEW_STATS.totalReviews} verified reviews from Facebook &amp; Rate My Agent</span>
          </div>
        </div>

        {/* Carousel */}
        <div
          className="relative"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div className="overflow-hidden" data-testid="reviews-carousel">
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${translate}%)` }}
            >
              {REVIEWS.map((r) => (
                <div
                  key={r.id}
                  className="flex-shrink-0 px-3"
                  style={{ flexBasis: `${slideBasis}%` }}
                  data-testid={`review-card-${r.id}`}
                >
                  <article className="h-full bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-7 flex flex-col hover:shadow-md hover:border-red-200 transition-all">
                    <Quote className="w-8 h-8 text-red-500/20 mb-3 flex-shrink-0" />
                    <p className="text-gray-700 text-sm md:text-base leading-relaxed italic flex-1">
                      {r.text}
                    </p>
                    <div className="mt-5 pt-4 border-t border-gray-100">
                      <StarRow value={r.rating} />
                      <p className="font-semibold text-gray-900 mt-2">{r.author}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {r.location && (
                          <span className="text-xs text-gray-500">{r.location}</span>
                        )}
                        {r.location && <span className="text-gray-300">·</span>}
                        <SourceBadge source={r.source} />
                      </div>
                    </div>
                  </article>
                </div>
              ))}
            </div>
          </div>

          {/* Prev / Next buttons */}
          <button
            type="button"
            onClick={goPrev}
            aria-label="Previous review"
            data-testid="reviews-prev"
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-4 bg-white hover:bg-red-600 hover:text-white text-gray-700 rounded-full h-10 w-10 md:h-12 md:w-12 flex items-center justify-center shadow-lg transition-colors border border-gray-200 z-10"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={goNext}
            aria-label="Next review"
            data-testid="reviews-next"
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-4 bg-white hover:bg-red-600 hover:text-white text-gray-700 rounded-full h-10 w-10 md:h-12 md:w-12 flex items-center justify-center shadow-lg transition-colors border border-gray-200 z-10"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-8" data-testid="reviews-dots">
          {Array.from({ length: pages }).map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`Go to review ${i + 1}`}
              data-testid={`reviews-dot-${i}`}
              className={`h-2 rounded-full transition-all ${
                i === index ? 'w-8 bg-red-600' : 'w-2 bg-gray-300 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ClientReviewsCarousel;
